// pages/CampaignsPage.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { 
  Megaphone, 
  Calendar, 
  MapPin, 
  Users, 
  Target, 
  Clock, 
  ChevronRight, 
  Heart, 
  Share2, 
  ArrowRight,
  Filter,
  Star,
  TrendingUp,
  CheckCircle,
  Award,
  Zap,
  ArrowLeft
} from 'lucide-react';

const CampaignsPage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [participatedCampaigns, setParticipatedCampaigns] = useState([]);
  const [bookmarkedCampaigns, setBookmarkedCampaigns] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isSmallScreen = viewportWidth < 390;
  const isTabletOrSmaller = viewportWidth < 768;

  const defaultCampaigns = [
    {
      id: 1,
      title: 'National Diabetes Screening Week',
      category: 'screening',
      organization: 'Ministry of Health',
      description: 'Get free blood sugar tests and comprehensive diabetes risk assessment at participating clinics nationwide. Early detection saves lives!',
      date: 'Dec 15-22, 2024',
      location: 'Nationwide',
      participants: 12450,
      target: 50000,
      progress: 85,
      urgency: 'high',
      status: 'active',
      bannerColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      icon: '🩺',
      benefits: ['Free comprehensive test', 'Doctor consultation', 'Personalized diet plan', 'Follow-up care'],
      highlights: ['Government-funded', 'No age limit', 'Instant results'],
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 2,
      title: 'Mental Health Awareness Drive 2024',
      category: 'awareness',
      organization: 'MindCare Foundation',
      description: 'Join our month-long initiative to promote mental wellness. Free counseling sessions, workshops, and community support groups.',
      date: 'Ongoing',
      location: 'Major Cities',
      participants: 8765,
      target: 15000,
      progress: 68,
      urgency: 'medium',
      status: 'active',
      bannerColor: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      icon: '🧠',
      benefits: ['Free therapy sessions', 'Support groups', 'Mental health resources', 'Stress management workshops'],
      highlights: ['Certified therapists', 'Anonymous support', '24/7 helpline'],
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 3,
      title: 'Annual Vaccination Drive',
      category: 'vaccination',
      organization: 'City Health Services',
      description: 'Free flu vaccinations for seniors and high-risk individuals. Protect yourself and your community this flu season.',
      date: 'Jan 10-25, 2024',
      location: 'City Hospitals',
      participants: 32100,
      target: 50000,
      progress: 92,
      urgency: 'high',
      status: 'active',
      bannerColor: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      icon: '💉',
      benefits: ['Free vaccine', 'Health checkup', 'Immunization card', 'Prevention kit'],
      highlights: ['For all age groups', 'Priority for seniors', 'Walk-in available'],
      image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 4,
      title: 'Blood Donation Marathon',
      category: 'donation',
      organization: 'Red Cross Society',
      description: '24-hour blood donation event to replenish emergency blood supplies. Your donation can save up to 3 lives!',
      date: 'Feb 14, 2024',
      location: 'Central Blood Bank',
      participants: 5432,
      target: 10000,
      progress: 74,
      urgency: 'medium',
      status: 'upcoming',
      bannerColor: 'linear-gradient(135deg, #f43b47 0%, #453a94 100%)',
      icon: '🩸',
      benefits: ['Health screening', 'Donor appreciation kit', 'Refreshments', 'Certificate of appreciation'],
      highlights: ['Emergency reserve', 'Team donations', 'Special rewards'],
      image: 'https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 5,
      title: 'Healthy Heart Initiative',
      category: 'prevention',
      organization: 'Cardiac Care Association',
      description: 'Free heart health checkups and preventive care education. Learn about heart-healthy living and early warning signs.',
      date: 'Mar 1-31, 2024',
      location: 'Partner Clinics',
      participants: 15432,
      target: 30000,
      progress: 52,
      urgency: 'low',
      status: 'active',
      bannerColor: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      icon: '❤️',
      benefits: ['Free ECG test', 'Cholesterol check', 'Diet consultation', 'Exercise plan'],
      highlights: ['Cardiologist consultation', 'Family packages', 'Lifestyle coaching'],
      image: 'https://images.unsplash.com/photo-1583454110551-21f2fa2afe61?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 6,
      title: 'Cancer Early Detection Camp',
      category: 'screening',
      organization: 'Oncology Research Institute',
      description: 'Free screenings for early cancer detection. Early diagnosis significantly improves treatment outcomes.',
      date: 'Apr 5-10, 2024',
      location: 'Research Centers',
      participants: 0,
      target: 8000,
      progress: 0,
      urgency: 'medium',
      status: 'upcoming',
      bannerColor: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
      icon: '🔬',
      benefits: ['Free screening', 'Expert consultation', 'Genetic counseling', 'Follow-up care'],
      highlights: ['Advanced technology', 'Family history analysis', 'Preventive guidance'],
      image: 'https://images.unsplash.com/photo-1559757175-0eb30cd8c063?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 7,
      title: 'Child Immunization Program',
      category: 'vaccination',
      organization: 'UNICEF & Health Department',
      description: 'Free immunization for children under 5 years. Protect your child from preventable diseases.',
      date: 'May 1-15, 2024',
      location: 'Community Centers',
      participants: 0,
      target: 25000,
      progress: 0,
      urgency: 'high',
      status: 'upcoming',
      bannerColor: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      icon: '👶',
      benefits: ['Free vaccines', 'Growth monitoring', 'Nutrition guidance', 'Parent education'],
      highlights: ['All essential vaccines', 'Certified pediatricians', 'Post-vaccination care'],
      image: 'https://images.unsplash.com/photo-1582750433449-648ed127bb54?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 8,
      title: 'Organ Donation Awareness Month',
      category: 'awareness',
      organization: 'National Organ Transplant Network',
      description: 'Raise awareness about organ donation. Register as an organ donor and give the gift of life.',
      date: 'Jun 1-30, 2024',
      location: 'Online & Hospitals',
      participants: 876,
      target: 5000,
      progress: 32,
      urgency: 'medium',
      status: 'active',
      bannerColor: 'linear-gradient(135deg, #5ee7df 0%, #b490ca 100%)',
      icon: '🫀',
      benefits: ['Donor registration', 'Medical checkup', 'Family counseling', 'Legal guidance'],
      highlights: ['Life-saving initiative', 'Comprehensive support', 'National registry'],
      image: 'https://images.unsplash.com/photo-1516549655669-df565bc5e5bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    }
  ];

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setIsLoadingCampaigns(true);
        const res = await api.get('/api/campaigns');
        const list = Array.isArray(res?.data?.campaigns) ? res.data.campaigns : [];

        if (!list.length) {
          setCampaigns(defaultCampaigns);
          return;
        }

        const normalized = list.map((campaign) => ({
          id: campaign._id || campaign.id,
          title: campaign.title || 'Untitled Campaign',
          category: campaign.category || 'awareness',
          organization: campaign.organization || 'Qureo',
          description: campaign.description || '',
          date: campaign.date || 'TBD',
          location: campaign.location || 'Unknown',
          participants: Number(campaign.participants || 0),
          target: Number(campaign.target || 0),
          progress: Number(campaign.progress || 0),
          urgency: campaign.urgency || 'medium',
          status: campaign.status || 'upcoming',
          bannerColor: campaign.bannerColor || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          icon: campaign.icon || '📢',
          benefits: Array.isArray(campaign.benefits) ? campaign.benefits : [],
          highlights: Array.isArray(campaign.highlights) ? campaign.highlights : [],
          image: campaign.image || '',
        }));

        setCampaigns(normalized);
      } catch (_error) {
        setCampaigns(defaultCampaigns);
      } finally {
        setIsLoadingCampaigns(false);
      }
    };

    fetchCampaigns();
  }, []);

  const categories = [
    { id: 'all', label: 'All Campaigns', icon: '📋', count: campaigns.length },
    { id: 'screening', label: 'Screenings', icon: '🩺', count: campaigns.filter(c => c.category === 'screening').length },
    { id: 'vaccination', label: 'Vaccinations', icon: '💉', count: campaigns.filter(c => c.category === 'vaccination').length },
    { id: 'awareness', label: 'Awareness', icon: '📢', count: campaigns.filter(c => c.category === 'awareness').length },
    { id: 'donation', label: 'Donations', icon: '🩸', count: campaigns.filter(c => c.category === 'donation').length },
    { id: 'prevention', label: 'Prevention', icon: '🛡️', count: campaigns.filter(c => c.category === 'prevention').length }
  ];

  const statusFilters = [
    { id: 'all', label: 'All Status', count: campaigns.length },
    { id: 'active', label: 'Active', count: campaigns.filter(c => c.status === 'active').length },
    { id: 'upcoming', label: 'Upcoming', count: campaigns.filter(c => c.status === 'upcoming').length },
    { id: 'completed', label: 'Completed', count: campaigns.filter(c => c.status === 'completed').length }
  ];

  const filteredCampaigns = campaigns.filter(campaign => {
    const categoryOk = selectedCategory === 'all' ? true : campaign.category === selectedCategory;
    const statusOk = selectedStatus === 'all' ? true : campaign.status === selectedStatus;
    return categoryOk && statusOk;
  });

  const handleParticipate = (campaignId) => {
    if (participatedCampaigns.includes(campaignId)) {
      setParticipatedCampaigns(participatedCampaigns.filter(id => id !== campaignId));
    } else {
      setParticipatedCampaigns([...participatedCampaigns, campaignId]);
    }
  };

  const handleBookmark = (campaignId) => {
    if (bookmarkedCampaigns.includes(campaignId)) {
      setBookmarkedCampaigns(bookmarkedCampaigns.filter(id => id !== campaignId));
    } else {
      setBookmarkedCampaigns([...bookmarkedCampaigns, campaignId]);
    }
  };

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case 'active': return { backgroundColor: '#10b981', color: '#ffffff' };
      case 'upcoming': return { backgroundColor: '#f59e0b', color: '#ffffff' };
      case 'completed': return { backgroundColor: '#6b7280', color: '#ffffff' };
      default: return { backgroundColor: '#9ca3af', color: '#ffffff' };
    }
  };

  const getUrgencyBadgeStyle = (urgency) => {
    switch (urgency) {
      case 'high': return { backgroundColor: '#ef4444', color: '#ffffff' };
      case 'medium': return { backgroundColor: '#f59e0b', color: '#ffffff' };
      case 'low': return { backgroundColor: '#10b981', color: '#ffffff' };
      default: return { backgroundColor: '#9ca3af', color: '#ffffff' };
    }
  };

  // Page styles
  const pageStyles = {
    padding: isSmallScreen ? '14px 10px 74px' : '20px 16px 80px',
    background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
    minHeight: '100vh'
  };

  const pageContainerStyles = {
    maxWidth: '1240px',
    margin: '0 auto',
  };

  const headerStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '32px',
    flexWrap: 'wrap',
    gap: isSmallScreen ? '12px' : '16px',
    position: 'relative',
    backgroundColor: '#ffffff',
    borderRadius: '18px',
    padding: isSmallScreen ? '12px 12px 14px' : '18px 20px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 18px rgba(15, 23, 42, 0.05)'
  };

  const backButtonStyles = {
    position: 'static',
    padding: '0',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    color: '#7c3aed',
    marginTop: 4,
    flexShrink: 0,
  };

  const titleSectionStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginLeft: 0
  };

  const titleStyles = {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
    lineHeight: 1.15,
  };

  const subtitleStyles = {
    fontSize: '12px',
    color: '#64748b',
    marginTop: '4px',
    marginLeft: '34px',
  };

  const statsGridStyles = {
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fit, minmax(${isSmallScreen ? '160px' : '240px'}, 1fr))`,
    gap: isSmallScreen ? '10px' : '20px',
    marginBottom: '28px'
  };

  const statCardStyles = {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: isSmallScreen ? '12px' : '20px',
    boxShadow: '0 6px 20px rgba(15, 23, 42, 0.06)',
    display: 'flex',
    alignItems: 'center',
    gap: isSmallScreen ? '10px' : '16px',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    border: '1px solid #e2e8f0'
  };

  const statIconWrapperStyles = {
    width: isSmallScreen ? '42px' : '56px',
    height: isSmallScreen ? '42px' : '56px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: isSmallScreen ? '18px' : '24px'
  };

  const statContentStyles = {
    flex: 1
  };

  const statLabelStyles = {
    fontSize: isSmallScreen ? '11px' : '14px',
    color: '#64748b',
    marginBottom: '4px'
  };

  const statValueStyles = {
    fontSize: isSmallScreen ? '18px' : '24px',
    fontWeight: '700',
    color: '#1e293b'
  };

  const filtersContainerStyles = {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: isSmallScreen ? '14px' : '24px',
    marginBottom: '28px',
    boxShadow: '0 6px 20px rgba(15, 23, 42, 0.06)',
    border: '1px solid #e2e8f0'
  };

  const filtersHeaderStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px'
  };

  const filterGroupStyles = {
    marginBottom: '24px'
  };

  const filterGroupTitleStyles = {
    fontSize: isSmallScreen ? '15px' : '18px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '16px'
  };

  const filterButtonsContainerStyles = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: isSmallScreen ? '8px' : '12px'
  };

  const filterButtonStyles = (isActive) => ({
    padding: isSmallScreen ? '8px 12px' : '12px 20px',
    borderRadius: '12px',
    border: `1px solid ${isActive ? '#7c3aed' : '#e2e8f0'}`,
    backgroundColor: isActive ? '#7c3aed' : '#ffffff',
    color: isActive ? '#ffffff' : '#475569',
    fontSize: isSmallScreen ? '12px' : '14px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s ease'
  });

  const campaignsGridStyles = {
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, minmax(${isSmallScreen ? '250px' : '340px'}, 1fr))`,
    gap: isSmallScreen ? '14px' : '24px'
  };

  const campaignCardStyles = {
    backgroundColor: '#ffffff',
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    border: '1px solid #e5e7eb'
  };

  const campaignBannerStyles = (bannerColor, image) => ({
    height: isSmallScreen ? '150px' : '180px',
    background: `${bannerColor}, url(${image})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundBlendMode: 'overlay',
    position: 'relative',
    padding: isSmallScreen ? '14px' : '24px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    boxShadow: 'inset 0 -70px 120px rgba(15, 23, 42, 0.32)'
  });

  const campaignStatusBadgeStyles = (status) => ({
    position: 'absolute',
    top: isSmallScreen ? '10px' : '16px',
    right: isSmallScreen ? '10px' : '16px',
    padding: isSmallScreen ? '4px 8px' : '6px 12px',
    borderRadius: '20px',
    fontSize: isSmallScreen ? '10px' : '12px',
    fontWeight: '600',
    ...getStatusBadgeStyle(status)
  });

  const campaignHeaderStyles = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: isSmallScreen ? '8px' : '12px',
    marginBottom: isSmallScreen ? '8px' : '12px'
  };

  const campaignIconStyles = {
    fontSize: isSmallScreen ? '24px' : '32px',
    marginTop: '4px'
  };

  const campaignTitleStyles = {
    fontSize: isSmallScreen ? '16px' : '20px',
    fontWeight: '700',
    color: '#ffffff',
    margin: 0,
    lineHeight: 1.3,
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
  };

  const campaignOrgStyles = {
    fontSize: isSmallScreen ? '12px' : '14px',
    color: 'rgba(255, 255, 255, 0.9)',
    marginTop: '4px'
  };

  const campaignContentStyles = {
    padding: isSmallScreen ? '14px' : '20px'
  };

  const campaignDescriptionStyles = {
    fontSize: isSmallScreen ? '12px' : '14px',
    color: '#475569',
    lineHeight: 1.6,
    marginBottom: '18px',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  };

  const campaignMetaStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: isSmallScreen ? '10px' : '16px',
    marginBottom: '20px',
    flexWrap: 'wrap'
  };

  const metaItemStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: isSmallScreen ? '11px' : '13px',
    color: '#64748b'
  };

  const progressContainerStyles = {
    marginBottom: '20px'
  };

  const progressHeaderStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  };

  const progressLabelStyles = {
    fontSize: isSmallScreen ? '12px' : '14px',
    color: '#475569',
    fontWeight: '500'
  };

  const progressValueStyles = {
    fontSize: isSmallScreen ? '12px' : '14px',
    color: '#7c3aed',
    fontWeight: '600'
  };

  const progressBarStyles = {
    height: '8px',
    backgroundColor: '#e2e8f0',
    borderRadius: '4px',
    overflow: 'hidden'
  };

  const progressFillStyles = (progress) => ({
    height: '100%',
    width: `${progress}%`,
    backgroundColor: '#7c3aed',
    borderRadius: '4px',
    transition: 'width 0.5s ease'
  });

  const progressStatsStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: isSmallScreen ? '10px' : '12px',
    color: '#94a3b8',
    marginTop: '4px'
  };

  const highlightsContainerStyles = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '20px'
  };

  const highlightChipStyles = {
    padding: isSmallScreen ? '4px 8px' : '6px 12px',
    backgroundColor: '#eef2ff',
    borderRadius: '20px',
    fontSize: isSmallScreen ? '10px' : '12px',
    color: '#4338ca',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    border: '1px solid #c7d2fe'
  };

  const actionButtonsStyles = {
    display: 'flex',
    gap: isSmallScreen ? '8px' : '12px',
    marginTop: '18px',
    flexWrap: isSmallScreen ? 'wrap' : 'nowrap'
  };

  const primaryButtonStyles = (isParticipating) => ({
    flex: 1,
    minWidth: isSmallScreen ? '100%' : 'auto',
    padding: isSmallScreen ? '10px 12px' : '12px 20px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: isParticipating ? '#10b981' : '#7c3aed',
    color: '#ffffff',
    fontSize: isSmallScreen ? '13px' : '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.2s ease'
  });

  const secondaryButtonStyles = {
    padding: isSmallScreen ? '10px' : '12px',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#ffffff',
    color: '#64748b',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.05)'
  };

  const bookmarkButtonStyles = (isBookmarked) => ({
    padding: isSmallScreen ? '10px' : '12px',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#ffffff',
    color: isBookmarked ? '#ef4444' : '#64748b',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.05)'
  });

  const emptyStateStyles = {
    gridColumn: '1 / -1',
    textAlign: 'center',
    padding: '60px 20px'
  };

  const emptyStateIconStyles = {
    fontSize: '64px',
    marginBottom: '20px',
    opacity: 0.5
  };

  const emptyStateTextStyles = {
    fontSize: '18px',
    color: '#64748b',
    marginBottom: '12px'
  };

  const emptyStateSubtextStyles = {
    fontSize: '14px',
    color: '#94a3b8'
  };

  return (
    <div style={pageStyles}>
      <div style={pageContainerStyles}>
      {/* Header */}
      <div style={headerStyles}>
        <button
          style={backButtonStyles}
          onClick={() => navigate(-1)}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = '#f1f5f9';
            e.currentTarget.style.borderColor = '#cbd5e1';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = '#ffffff';
            e.currentTarget.style.borderColor = '#e2e8f0';
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <div style={titleSectionStyles}>
            <Megaphone size={32} color="#7c3aed" />
            <h1 style={titleStyles}>Health Campaigns</h1>
          </div>
          <p style={subtitleStyles}>
            Join impactful health initiatives and make a difference in your community
          </p>
        </div>
        <button style={{
          padding: isSmallScreen ? '10px 12px' : '12px 24px',
          backgroundColor: '#7c3aed',
          color: '#ffffff',
          border: 'none',
          borderRadius: '12px',
          fontSize: isSmallScreen ? '12px' : '14px',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: isSmallScreen ? '6px' : '8px',
          transition: 'all 0.2s ease'
        }}
        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#6d28d9'}
        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#7c3aed'}>
          <Megaphone size={18} />
          Suggest a Campaign
        </button>
      </div>

      {/* Stats Overview */}
      <div style={statsGridStyles}>
        <div 
          style={statCardStyles}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
          }}
        >
          <div style={{ ...statIconWrapperStyles, backgroundColor: '#e0e7ff' }}>
            <Megaphone size={28} color="#4f46e5" />
          </div>
          <div style={statContentStyles}>
            <div style={statLabelStyles}>Active Campaigns</div>
            <div style={statValueStyles}>{campaigns.filter(c => c.status === 'active').length}</div>
          </div>
        </div>

        <div 
          style={statCardStyles}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
          }}
        >
          <div style={{ ...statIconWrapperStyles, backgroundColor: '#dcfce7' }}>
            <Users size={28} color="#16a34a" />
          </div>
          <div style={statContentStyles}>
            <div style={statLabelStyles}>Total Participants</div>
            <div style={statValueStyles}>68,459</div>
          </div>
        </div>

        <div 
          style={statCardStyles}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
          }}
        >
          <div style={{ ...statIconWrapperStyles, backgroundColor: '#fef3c7' }}>
            <Target size={28} color="#d97706" />
          </div>
          <div style={statContentStyles}>
            <div style={statLabelStyles}>Your Participation</div>
            <div style={statValueStyles}>{participatedCampaigns.length}</div>
          </div>
        </div>

        <div 
          style={statCardStyles}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
          }}
        >
          <div style={{ ...statIconWrapperStyles, backgroundColor: '#f3e8ff' }}>
            <TrendingUp size={28} color="#7c3aed" />
          </div>
          <div style={statContentStyles}>
            <div style={statLabelStyles}>Success Rate</div>
            <div style={statValueStyles}>94%</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={filtersContainerStyles}>
        <div style={filtersHeaderStyles}>
          <Filter size={20} color="#475569" />
          <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 }}>
            Filter Campaigns
          </h3>
        </div>

        <div style={filterGroupStyles}>
          <h4 style={filterGroupTitleStyles}>By Category</h4>
          <div style={filterButtonsContainerStyles}>
            {categories.map((category) => (
              <button
                key={category.id}
                style={filterButtonStyles(selectedCategory === category.id)}
                onClick={() => setSelectedCategory(category.id)}
                onMouseOver={(e) => {
                  if (selectedCategory !== category.id) {
                    e.currentTarget.style.backgroundColor = '#f8fafc';
                    e.currentTarget.style.borderColor = '#cbd5e1';
                  }
                }}
                onMouseOut={(e) => {
                  if (selectedCategory !== category.id) {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }
                }}
              >
                <span style={{ fontSize: '18px' }}>{category.icon}</span>
                {category.label}
                <span style={{ 
                  fontSize: '12px', 
                  backgroundColor: selectedCategory === category.id ? 'rgba(255,255,255,0.2)' : '#f1f5f9',
                  color: selectedCategory === category.id ? '#ffffff' : '#64748b',
                  padding: '2px 8px',
                  borderRadius: '10px'
                }}>
                  {category.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div style={filterGroupStyles}>
          <h4 style={filterGroupTitleStyles}>By Status</h4>
          <div style={filterButtonsContainerStyles}>
            {statusFilters.map((status) => (
              <button
                key={status.id}
                style={filterButtonStyles(selectedStatus === status.id)}
                onClick={() => setSelectedStatus(status.id)}
                onMouseOver={(e) => {
                  if (selectedStatus !== status.id) {
                    e.currentTarget.style.backgroundColor = '#f8fafc';
                    e.currentTarget.style.borderColor = '#cbd5e1';
                  }
                }}
                onMouseOut={(e) => {
                  if (selectedStatus !== status.id) {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }
                }}
              >
                {status.label}
                <span style={{ 
                  fontSize: '12px', 
                  backgroundColor: '#f1f5f9',
                  color: '#64748b',
                  padding: '2px 8px',
                  borderRadius: '10px'
                }}>
                  {status.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Campaigns Grid */}
      <div style={campaignsGridStyles}>
        {isLoadingCampaigns && (
          <div style={emptyStateStyles}>
            <div style={emptyStateIconStyles}>⏳</div>
            <h3 style={emptyStateTextStyles}>Loading campaigns...</h3>
          </div>
        )}

        {filteredCampaigns.map((campaign) => {
          const isParticipating = participatedCampaigns.includes(campaign.id);
          const isBookmarked = bookmarkedCampaigns.includes(campaign.id);

          return (
            <div
              key={campaign.id}
              style={campaignCardStyles}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)';
              }}
            >
              {/* Campaign Banner */}
              <div style={campaignBannerStyles(campaign.bannerColor, campaign.image)}>
                <div style={campaignStatusBadgeStyles(campaign.status)}>
                  {campaign.status.toUpperCase()}
                </div>
                
                <div>
                  <div style={campaignHeaderStyles}>
                    <span style={campaignIconStyles}>{campaign.icon}</span>
                    <div>
                      <h3 style={campaignTitleStyles}>{campaign.title}</h3>
                      <p style={campaignOrgStyles}>{campaign.organization}</p>
                    </div>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap'
                }}>
                  <span style={{
                    padding: '4px 10px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    fontSize: '11px',
                    color: '#ffffff',
                    backdropFilter: 'blur(4px)'
                  }}>
                    🎯 {campaign.target.toLocaleString()} Target
                  </span>
                  <span style={getUrgencyBadgeStyle(campaign.urgency)}>
                    {campaign.urgency.toUpperCase()} PRIORITY
                  </span>
                </div>
              </div>

              {/* Campaign Content */}
              <div style={campaignContentStyles}>
                <p style={campaignDescriptionStyles}>
                  {campaign.description}
                </p>

                {/* Campaign Metadata */}
                <div style={campaignMetaStyles}>
                  <div style={metaItemStyles}>
                    <Calendar size={14} />
                    <span>{campaign.date}</span>
                  </div>
                  <div style={metaItemStyles}>
                    <MapPin size={14} />
                    <span>{campaign.location}</span>
                  </div>
                  <div style={metaItemStyles}>
                    <Users size={14} />
                    <span>{campaign.participants.toLocaleString()} joined</span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div style={progressContainerStyles}>
                  <div style={progressHeaderStyles}>
                    <span style={progressLabelStyles}>Campaign Progress</span>
                    <span style={progressValueStyles}>{campaign.progress}%</span>
                  </div>
                  <div style={progressBarStyles}>
                    <div style={progressFillStyles(campaign.progress)} />
                  </div>
                  <div style={progressStatsStyles}>
                    <span>{campaign.participants.toLocaleString()} participants</span>
                    <span>Goal: {campaign.target.toLocaleString()}</span>
                  </div>
                </div>

                {/* Highlights */}
                <div style={highlightsContainerStyles}>
                  {campaign.highlights.map((highlight, index) => (
                    <div key={index} style={highlightChipStyles}>
                      <Zap size={10} />
                      {highlight}
                    </div>
                  ))}
                </div>

                {/* Benefits */}
                <div style={{
                  marginBottom: '20px',
                  padding: '12px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '12px'
                }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#475569',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <Award size={14} />
                    Key Benefits
                  </div>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}>
                    {campaign.benefits.slice(0, 3).map((benefit, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '12px',
                        color: '#64748b'
                      }}>
                        <CheckCircle size={12} color="#10b981" />
                        {benefit}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={actionButtonsStyles}>
                  <button
                    style={primaryButtonStyles(isParticipating)}
                    onClick={() => handleParticipate(campaign.id)}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = isParticipating ? '#0ea271' : '#6d28d9';
                      e.currentTarget.style.transform = 'scale(1.02)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = isParticipating ? '#10b981' : '#7c3aed';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    {isParticipating ? (
                      <>
                        <CheckCircle size={16} />
                        Participating
                      </>
                    ) : (
                      <>
                        Join Campaign
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>

                  <button
                    style={bookmarkButtonStyles(isBookmarked)}
                    onClick={() => handleBookmark(campaign.id)}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                      e.currentTarget.style.borderColor = '#cbd5e1';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                    }}
                  >
                    <Heart size={18} fill={isBookmarked ? 'currentColor' : 'none'} />
                  </button>

                  <button
                    style={secondaryButtonStyles}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                      e.currentTarget.style.borderColor = '#cbd5e1';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                    }}
                  >
                    <Share2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {filteredCampaigns.length === 0 && (
          <div style={emptyStateStyles}>
            <div style={emptyStateIconStyles}>📋</div>
            <h3 style={emptyStateTextStyles}>No campaigns found</h3>
            <p style={emptyStateSubtextStyles}>
              Try selecting a different category or check back later for new campaigns
            </p>
          </div>
        )}
      </div>

      {/* Quick Stats Footer */}
      <div style={{
        marginTop: '48px',
        padding: '24px',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #312e81 100%)',
        borderRadius: '20px',
        color: '#ffffff',
        border: '1px solid rgba(148, 163, 184, 0.18)',
        boxShadow: '0 14px 38px rgba(15, 23, 42, 0.34)'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(${isTabletOrSmaller ? '140px' : '200px'}, 1fr))`,
          gap: isSmallScreen ? '14px' : '24px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#60a5fa' }}>150K+</div>
            <div style={{ fontSize: '14px', color: '#cbd5e1' }}>Lives Impacted</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#34d399' }}>89%</div>
            <div style={{ fontSize: '14px', color: '#cbd5e1' }}>Success Rate</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#fbbf24' }}>45</div>
            <div style={{ fontSize: '14px', color: '#cbd5e1' }}>Partner Organizations</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#a78bfa' }}>24/7</div>
            <div style={{ fontSize: '14px', color: '#cbd5e1' }}>Support Available</div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default CampaignsPage;